import React, { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { FieldCatalog, WizardState } from './types';
import { buildTemplateRows, getSelectedColumns } from './generateTemplateCsv';
import type { CatalogTemplateItemRow } from './fetchCatalogItemsForTemplate';

interface ReviewStepProps {
  state: WizardState;
  catalogItems: CatalogTemplateItemRow[];
  isLoading: boolean;
  catalog: FieldCatalog;
}

const MAX_PREVIEW_ROWS = 50;

const ReviewStep = ({ state, catalogItems, isLoading, catalog }: ReviewStepProps) => {
  const isCreate = state.purpose === 'create';
  const columns = useMemo(() => getSelectedColumns(state, catalog), [state, catalog]);
  const rows = useMemo(
    () => (isLoading ? [] : buildTemplateRows(catalogItems, state, catalog)),
    [catalogItems, state, catalog, isLoading],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading catalog items...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {isCreate ? (
          <Chip label="Header row only" size="small" />
        ) : (
          <>
            <Chip label={`${rows.length} rows`} size="small" />
            <Chip label={state.scopeMode === 'all' ? 'All catalog records' : 'Subset of records'} size="small" />
          </>
        )}
        <Chip label={`${columns.length} columns`} size="small" />
        {!isCreate && (
          <Chip label={state.valuesMode === 'prefill' ? 'Current values' : 'Blank values'} size="small" />
        )}
      </Box>

      {isCreate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The template will contain the header row only. Add one row for each new catalog record you want to create.
        </Alert>
      )}

      {!isCreate && rows.length === 0 ? (
        <Alert severity="warning">
          No records match your selections. Go back and widen the scope.
        </Alert>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 340 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map(col => (
                    <TableCell key={col.csvColumn}>{col.csvColumn}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, MAX_PREVIEW_ROWS).map((row, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={i}>
                    {columns.map(col => (
                      <TableCell key={col.csvColumn} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                        {row[col.csvColumn] || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {rows.length > MAX_PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {`Showing ${MAX_PREVIEW_ROWS} of ${rows.length} rows`}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ReviewStep;
